import { keepPreviousData, useQuery } from "@tanstack/react-query";
import URI from "urijs";
import concat from "lodash/concat";
import { EventsNotifications } from "@graylog/server-api";
import i18n from "i18n";
import { qualifyUrl } from "util/URLUtils";
import UserNotification from "util/UserNotification";
import fetch from "logic/rest/FetchProvider";
import CancellablePromise from "logic/rest/CancellablePromise";
import { defaultOnError } from "util/conditional/onError";
export const EVENT_NOTIFICATIONS_QUERY_KEY = ["event-notifications"];
const sourceUrl = "/events/notifications";
const eventNotificationsUrl = ({
  segments = [],
  query = {}
}) => {
  const uri = new URI(sourceUrl);
  const nextSegments = concat(uri.segment(), segments);
  uri.segmentCoded(nextSegments);
  uri.query(query);
  return qualifyUrl(uri.resource());
};
export const fetchEventNotifications = (searchParams) => EventsNotifications.getPage(
  searchParams.sort.attributeId,
  searchParams.page,
  searchParams.pageSize,
  searchParams.query || void 0,
  searchParams.sort.direction
).then(({ elements, query, attributes, pagination }) => ({
  list: elements,
  pagination: {
    count: pagination.count,
    total: pagination.total,
    page: pagination.page,
    perPage: pagination.per_page,
    query
  },
  attributes
}));
export const fetchAllEventNotifications = () => fetch("GET", eventNotificationsUrl({ query: { per_page: 0 } }));
export const fetchLegacyEventNotificationTypes = () => fetch("GET", eventNotificationsUrl({ segments: ["legacy", "types"] }));
export const getEventNotification = (notificationId) => EventsNotifications.get(encodeURIComponent(notificationId)).then(
  (response) => response,
  (error) => {
    if (error.status === 404) {
      UserNotification.error(
        i18n.t("messages.not_found", { ns: "event_notifications", id: notificationId }),
        i18n.t("messages.not_found_title", { ns: "event_notifications" })
      );
    }
    throw error;
  }
);
export const createEventNotification = (notification) => {
  const { share_request, ...rest } = notification;
  return fetch("POST", eventNotificationsUrl({}), { entity: rest, share_request }).then(
    (response) => {
      UserNotification.success(
        i18n.t("messages.created", { ns: "event_notifications" }),
        i18n.t("messages.created_title", { ns: "event_notifications", title: notification.title })
      );
      return response;
    },
    (error) => {
      if (error.status !== 400 || !error.additional.body || !error.additional.body.failed) {
        UserNotification.error(
          i18n.t("messages.create_failed", {
            ns: "event_notifications",
            title: notification.title,
            error: String(error)
          }),
          i18n.t("messages.create_failed_title", { ns: "event_notifications" })
        );
      }
      throw error;
    }
  );
};
export const updateEventNotification = (notificationId, notification) => EventsNotifications.update(encodeURIComponent(notificationId), notification).then(
  (response) => {
    UserNotification.success(
      i18n.t("messages.updated", { ns: "event_notifications" }),
      i18n.t("messages.updated_title", { ns: "event_notifications", title: notification.title })
    );
    return response;
  },
  (error) => {
    if (error.status !== 400 || !error.additional.body || !error.additional.body.failed) {
      UserNotification.error(
        i18n.t("messages.update_failed", {
          ns: "event_notifications",
          title: notification.title,
          error: String(error)
        }),
        i18n.t("messages.update_failed_title", { ns: "event_notifications" })
      );
    }
    throw error;
  }
);
export const deleteEventNotification = (notification) => EventsNotifications.remove(encodeURIComponent(notification.id)).then(
  (response) => {
    UserNotification.success(
      i18n.t("messages.deleted", { ns: "event_notifications" }),
      i18n.t("messages.deleted_title", { ns: "event_notifications", title: notification.title })
    );
    return response;
  },
  (error) => {
    UserNotification.error(
      i18n.t("messages.delete_failed", {
        ns: "event_notifications",
        title: notification.title,
        error: String(error)
      }),
      i18n.t("messages.delete_failed_title", { ns: "event_notifications" })
    );
    throw error;
  }
);
export const testEventNotification = (notification) => CancellablePromise.of(EventsNotifications.test(notification));
export const testPersistedEventNotification = (notification) => EventsNotifications.testBynotificationId(encodeURIComponent(notification.id));
export const keyFn = (searchParams) => [
  ...EVENT_NOTIFICATIONS_QUERY_KEY,
  "overview-paginated",
  ...searchParams ? [searchParams] : []
];
export const useEventNotificationsPaginated = (searchParams, { enabled } = { enabled: true }) => {
  const { data, refetch, isInitialLoading } = useQuery({
    queryKey: keyFn(searchParams),
    queryFn: () => defaultOnError(
      fetchEventNotifications(searchParams),
      i18n.t("messages.load_all_failed", { ns: "event_notifications" }),
      i18n.t("messages.load_all_failed_title", { ns: "event_notifications" })
    ),
    placeholderData: keepPreviousData,
    retry: false,
    enabled
  });
  return {
    data,
    refetch,
    isInitialLoading
  };
};
export const useEventNotifications = () => useQuery({
  queryKey: [...EVENT_NOTIFICATIONS_QUERY_KEY, "all"],
  queryFn: fetchAllEventNotifications
});
export const useLegacyEventNotificationTypes = () => useQuery({
  // Deliberately not under EVENT_NOTIFICATIONS_QUERY_KEY: these are static plugin type
  // descriptors, not event notification entity data, so they should not be refetched on
  // event notification mutations.
  queryKey: ["event-notification-legacy-types"],
  queryFn: fetchLegacyEventNotificationTypes
});
export const useEventNotification = (notificationId) => useQuery({
  queryKey: [...EVENT_NOTIFICATIONS_QUERY_KEY, "id", notificationId],
  queryFn: () => defaultOnError(
    getEventNotification(notificationId),
    i18n.t("messages.load_failed", { ns: "event_notifications" }),
    i18n.t("messages.load_failed_title", { ns: "event_notifications" })
  ),
  retry: false
});
export default useEventNotificationsPaginated;
